import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, Icons, SpinnerOverlay } from '@components/common';
import ProfileTabSection from '@components/common/ProfileTabSection';
import Attributes from '@components/routes/AdminPanel/AssetHistory/AssetHistoryProfile/Attributes';
import Summary from '@components/routes/AdminPanel/AssetHistory/AssetHistoryProfile/Summary';

import { useQueryAssetById } from '@hooks/assets/useQueryAssetById';
import { useAssetHistoryProfile } from '@hooks/assets/useQueryAssetHistory';

import { IHistoryVersion, TChangedAssetProfile } from '@interfaces/assets/assetHistory';

import { tabs, tabsNames } from '@components/routes/AdminPanel/AssetProfile/constants';

import ProfileHeader from './ProfileHeader/ProfileHeader';
import { getChangedFieldsRecursive } from './util';

import styles from './AssetHistoryProfile.module.scss';

const AssetHistoryProfile = () => {
	const navigate = useNavigate();

	const onBackClickHandler = () => navigate(-1);

	const { historyId, id } = useParams();

	const [currentTab, setCurrentTab] = useState(tabsNames.summary);

	const {
		currentVersion,
		previousVersion,
		isLoading,
	}: IHistoryVersion = useAssetHistoryProfile({ assetId: historyId });

	const { assetById: relevantProfile, isLoadingAssetById: isLoadingProfile } = useQueryAssetById(id, true);

	const currentAsset = currentVersion ? currentVersion.asset : null;
	const previousAsset = previousVersion ? previousVersion.asset : null;

	const profile = useMemo(() => getChangedFieldsRecursive({
		current: currentAsset,
		previous: previousAsset,
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}) as TChangedAssetProfile, [currentVersion, previousVersion]);

	const getCurrentTab = () => {
		switch (currentTab) {
			case tabsNames.summary:
				return <Summary profile={profile} />;
			case tabsNames.attributes:
				return <Attributes profile={profile} />;
			default:
				return <Attributes profile={profile} />;
		}
	};

	const version = currentVersion?.asset
		? {
			current: currentVersion.createdAt,
			previous: previousVersion?.createdAt,
			previousVersion: previousVersion?.version,
			currentVersion: currentVersion?.version,
			currentAsset: currentVersion.asset,
		}
		: {};

	return (
		<div className={styles.assetHistoryProfileContainer}>
			{(isLoading || isLoadingProfile) && <SpinnerOverlay />}
			<Button icon={Icons.arrowLeft()}
					onClick={onBackClickHandler}
					text='Back'
					withoutBackground
			/>
			{currentVersion?.asset && (
				<>
					{!!relevantProfile
						&& <ProfileHeader
							profile={profile}
							relevantProfile={relevantProfile}
							version={version}
						/>
					}

					<ProfileTabSection
						currentTab={currentTab}
						getCurrentTab={getCurrentTab}
						setCurrentTab={setCurrentTab}
						tabs={tabs}
					/>
				</>
			)}
		</div>
	);
};

export default AssetHistoryProfile;
